<template>
  <UModal
    :title="fileName"
    :description="t('nolsImport.preview.title')"
    class="max-w-[80rem] w-[95vw] h-[90vh] max-h-[90vh] flex flex-col"
    v-model:open="isOpen"
    :dismissible="true"
    :ui="{
      overlay: 'bg-gray-950/75 dark:bg-gray-950/90',
      header: 'flex items-center justify-between p-3 sm:px-4 min-h-12 bg-[#e9edf3] dark:bg-(--ui-bg-elevated) border-b border-(--ui-border)',
      body: 'p-0 sm:p-0 flex-1 min-h-0 overflow-hidden',
    }"
  >
    <template #header>
      <VisuallyHidden>
        <DialogTitle>{{ fileName }}</DialogTitle>
        <DialogDescription>{{ t('nolsImport.preview.title') }}</DialogDescription>
      </VisuallyHidden>
      
      <div class="flex items-center gap-2 min-w-0">
        <UIcon name="i-lucide-file-json" class="text-primary shrink-0 w-5 h-5" />
        <span class="font-semibold truncate text-base">{{ fileName }}</span>
      </div>
      
      <div class="flex items-center gap-2">
        <UButton
          :label="t('nolsImport.actions.validate')"
          icon="i-lucide-file-check"
          variant="soft"
          size="sm"
          class="cursor-pointer"
          :loading="isValidating"
          @click="emit('validate', fileName)"
        />
        <UButton
          :label="copied ? t('nolsImport.preview.copied') : t('nolsImport.preview.copyRaw')"
          :icon="copied ? 'i-lucide-check' : 'i-lucide-copy'"
          variant="soft"
          size="sm"
          class="cursor-pointer"
          @click="copyToClipboard"
        />
        <UButton
          color="gray"
          variant="ghost"
          icon="i-heroicons-x-mark"
          class="ml-1 cursor-pointer"
          tabindex="-1"
          @click="isOpen = false"
        />
      </div>
    </template>
    
    <template #body>
      <div class="flex flex-col h-full">
        <div class="flex items-center gap-2 px-4 py-2.5 border-b border-(--ui-border) bg-(--ui-bg) shrink-0">
          <div class="flex-1 max-w-md">
            <UInput
              v-model="searchQuery"
              icon="i-lucide-search"
              :placeholder="t('nolsImport.preview.search')"
              size="sm"
              class="w-full"
              @keydown.enter.prevent="navigateNext"
            />
          </div>
          
          <template v-if="searchQuery.trim()">
            <span v-if="matchCount > 0" class="text-xs text-(--ui-text-muted) whitespace-nowrap">
              {{ t('nolsImport.preview.matchCount', { current: currentMatchIdx + 1, total: matchCount }) }}
            </span>
            <span v-else class="text-xs text-(--ui-text-dimmed) whitespace-nowrap">
              {{ t('nolsImport.preview.noMatches') }}
            </span>
            <UButton
              icon="i-lucide-chevron-up"
              variant="ghost"
              size="xs"
              :disabled="matchCount === 0"
              class="cursor-pointer"
              @click="navigatePrev"
            />
            <UButton
              icon="i-lucide-chevron-down"
              variant="ghost"
              size="xs"
              :disabled="matchCount === 0"
              class="cursor-pointer"
              @click="navigateNext"
            />
          </template>
          
          <div class="border-l border-(--ui-border) h-5 mx-1" />
          
          <UButton
            icon="i-lucide-unfold-vertical"
            variant="ghost"
            size="xs"
            :title="t('nolsImport.preview.expandAll')"
            class="cursor-pointer"
            @click="expandAll"
          />
          <UButton
            icon="i-lucide-fold-vertical"
            variant="ghost"
            size="xs"
            :title="t('nolsImport.preview.collapseAll')"
            class="cursor-pointer"
            @click="collapseAll"
          />
        </div>
        
        <div ref="treeContainer" class="flex-1 overflow-auto font-mono text-sm leading-relaxed p-4">
          <template v-if="parsedJson !== undefined">
            <JsonNode
              ref="rootNode"
              :value="parsedJson"
              :depth="0"
              :search-query="activeSearch"
              :default-expand-depth="defaultExpandDepth"
            />
          </template>
          <template v-else>
            <pre class="whitespace-pre-wrap break-all text-(--ui-text-muted)">{{ fileContent }}</pre>
          </template>
        </div>
      </div>
    </template>
  </UModal>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { DialogTitle, DialogDescription, VisuallyHidden } from 'reka-ui'
import JsonNode from '@/components/nols/JsonNode.vue'

const { t } = useI18n()

const props = defineProps<{
  modelValue: boolean
  fileName: string
  fileContent: string
  isValidating?: boolean
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  'validate': [fileName: string]
}>()

const isOpen = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
})

const copied = ref(false)
const searchQuery = ref('')
const currentMatchIdx = ref(0)
const treeContainer = ref<HTMLElement | null>(null)
const rootNode = ref<InstanceType<typeof JsonNode> | null>(null)
const markElements = ref<Element[]>([])

const activeSearch = computed(() => searchQuery.value.trim().toLowerCase())

const parsedJson = computed(() => {
  try {
    return JSON.parse(props.fileContent)
  } catch {
    return undefined
  }
})

const isSingleKeyObject = (val: unknown): boolean => val !== null && typeof val === 'object' && !Array.isArray(val) && Object.keys(val as Record<string, unknown>).length === 1

const defaultExpandDepth = computed(() => {
  const json = parsedJson.value
  
  if (isSingleKeyObject(json)) {
    const inner = Object.values(json as Record<string, unknown>)[0]
    
    if (isSingleKeyObject(inner)) return 3
  }
  
  return 2
})

const matchCount = computed(() => markElements.value.length)

let observer: MutationObserver | null = null
let settleTimer: ReturnType<typeof setTimeout> | null = null
let copiedTimer: ReturnType<typeof setTimeout> | null = null

const waitForDomSettle = (): Promise<void> => {
  return new Promise((resolve) => {
    if (!treeContainer.value) {
      resolve()
      return
    }
    
    observer?.disconnect()
    
    if (settleTimer) clearTimeout(settleTimer)
    
    const scheduleResolve = () => {
      if (settleTimer) clearTimeout(settleTimer)
      
      settleTimer = setTimeout(() => {
        observer?.disconnect()
        observer = null
        resolve()
      }, 100)
    }
    
    observer = new MutationObserver(() => {
      scheduleResolve()
    })
    
    observer.observe(treeContainer.value, {
      childList: true,
      subtree: true
    })
    
    scheduleResolve()
  })
}

const refreshMarks = () => {
  if (!treeContainer.value) {
    markElements.value = []
    return
  }
  
  markElements.value = Array.from(treeContainer.value.querySelectorAll('mark'))
  highlightCurrent()
}

const highlightCurrent = () => {
  markElements.value.forEach((el, i) => {
    if (i === currentMatchIdx.value) {
      (el as HTMLElement).classList.add('ring-2', 'ring-primary')
    } else {
      (el as HTMLElement).classList.remove('ring-2', 'ring-primary')
    }
  })
}

const scrollToCurrent = () => {
  const el = markElements.value[currentMatchIdx.value]
  
  if (el) {
    el.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }
}

const waitForMarksToSettle = async () => {
  let lastCount = -1
  let retries = 0
  
  while (retries < 20) {
    await nextTick()
    await waitForDomSettle()
    refreshMarks()
    
    if (markElements.value.length === lastCount) break
    
    lastCount = markElements.value.length
    retries++
  }
}

const navigateNext = async () => {
  if (matchCount.value === 0) return

  if (markElements.value.length === 0) {
    await waitForMarksToSettle()
  }

  if (markElements.value.length === 0) return

  const current = markElements.value[currentMatchIdx.value] as HTMLElement | undefined
  const isCurrentVisible = current && current.classList.contains('ring-2')

  if (isCurrentVisible) {
    currentMatchIdx.value = (currentMatchIdx.value + 1) % markElements.value.length
  }

  highlightCurrent()
  scrollToCurrent()
}

const navigatePrev = async () => {
  if (matchCount.value === 0) return
  
  if (markElements.value.length === 0) {
    await waitForMarksToSettle()
  }
  
  if (markElements.value.length === 0) return
  
  currentMatchIdx.value = (currentMatchIdx.value - 1 + markElements.value.length) % markElements.value.length
  
  highlightCurrent()
  scrollToCurrent()
}

const expandAll = () => {
  rootNode.value?.setExpandedAll(true)
}

const collapseAll = () => {
  rootNode.value?.setExpandedAll(false)
}

watch(activeSearch, async () => {
  currentMatchIdx.value = 0

  await waitForMarksToSettle()
  scrollToCurrent()
})

watch(isOpen, (open) => {
  if (!open) {
    searchQuery.value = ''
    currentMatchIdx.value = 0
    markElements.value = []
    
    observer?.disconnect()
    observer = null
    
    if (settleTimer) { clearTimeout(settleTimer); settleTimer = null }
    if (copiedTimer) { clearTimeout(copiedTimer); copiedTimer = null }
  }
})

onBeforeUnmount(() => {
  observer?.disconnect()
  
  if (settleTimer) clearTimeout(settleTimer)
  if (copiedTimer) clearTimeout(copiedTimer)
})

const copyToClipboard = async () => {
  try {
    await navigator.clipboard.writeText(props.fileContent)
    
    copied.value = true
    
    if (copiedTimer) clearTimeout(copiedTimer)
    
    copiedTimer = setTimeout(() => { copied.value = false }, 2000)
  } catch (err) {
    console.error('Failed to copy', err)
  }
}
</script>